/**
 * Phone numbers the way people type them → the E.164 form Auth requires.
 *
 * Families type «۰۹۱۲ ۳۴۵ ۶۷۸۹», «09123456789», «9123456789», «00989123456789»
 * or «+98 912 345 6789»; Auth accepts only «+989123456789» and answers
 * anything else with an English E.164 error. Persian and Arabic-Indic digits
 * become Latin, spaces / dashes / dots / brackets are dropped, and an Iranian
 * mobile in any of those shapes is rewritten to +98. A number that already
 * starts with + (or 00) and is not Iranian is kept as E.164 for families abroad.
 */

const FA = '۰۱۲۳۴۵۶۷۸۹';
const AR = '٠١٢٣٤٥٦٧٨٩';

/** Persian / Arabic-Indic digits → Latin; everything else unchanged. */
export function latinDigits(text: string): string {
  return text.replace(/[۰-۹٠-٩]/g, (d) =>
    String(FA.indexOf(d) >= 0 ? FA.indexOf(d) : AR.indexOf(d)),
  );
}

/** The E.164 form of a typed phone number, or null when it cannot be one. */
export function toE164(input: string): string | null {
  const raw = latinDigits(input)
    .trim()
    .replace(/[\s\-.()‌‎‏]/g, ''); // also ZWNJ / LRM / RLM pasted from Persian text
  const plus = raw.startsWith('+') || raw.startsWith('00');
  const digits = raw.replace(/^\+|^00/, '');
  if (!/^\d+$/.test(digits)) return null;

  // Iranian mobile: 9xxxxxxxxx after an optional 0, 98 or +98 / 0098.
  const ir = digits.match(/^(?:98|0)?(9\d{9})$/);
  if (ir && (!plus || digits.startsWith('98'))) return `+98${ir[1]}`;
  // +98 that is not a mobile (too short, a landline): never an OTP number.
  if (digits.startsWith('98')) return null;

  // Another country, typed with its code.
  if (plus && /^[1-9]\d{7,14}$/.test(digits)) return `+${digits}`;
  return null;
}
